interface ActiveRequest {
  controller: AbortController
  detachCaller: () => void
}

let sessionGeneration = 0
const activeRequests = new Set<ActiveRequest>()

function abortReason(): DOMException {
  return new DOMException('Admin session invalidated', 'AbortError')
}

export interface AdminRequestLease {
  signal: AbortSignal
  assertCurrent: () => void
  release: () => void
}

export function beginAdminRequest(callerSignal?: AbortSignal): AdminRequestLease {
  const generation = sessionGeneration
  const controller = new AbortController()
  const onCallerAbort = () => controller.abort(callerSignal?.reason ?? abortReason())
  if (callerSignal?.aborted) onCallerAbort()
  else callerSignal?.addEventListener('abort', onCallerAbort, { once: true })

  const request: ActiveRequest = {
    controller,
    detachCaller: () => callerSignal?.removeEventListener('abort', onCallerAbort),
  }
  activeRequests.add(request)

  const assertCurrent = () => {
    if (controller.signal.aborted || generation !== sessionGeneration) {
      throw controller.signal.reason ?? abortReason()
    }
  }

  return {
    signal: controller.signal,
    assertCurrent,
    release: () => {
      request.detachCaller()
      activeRequests.delete(request)
    },
  }
}

export function invalidateAdminRequests(): void {
  sessionGeneration += 1
  const reason = abortReason()
  for (const request of [...activeRequests]) {
    request.controller.abort(reason)
    request.detachCaller()
    activeRequests.delete(request)
  }
}
